import { useEffect } from 'react'
import { create } from 'zustand'
import { fetchGeneralWriteResults } from '../../api/generalAgentChatApi'
import type { GeneralChatMessage } from '../../types'
import type { AssignTarget } from './assignStore'
import { newestAssigns, storedAssignsOf, type StoredAssigns } from './storedAssigns'

interface StoredAssignStore {
    /** The chat the assigns belong to. */
    tabId: string | null
    /** The stored writes as read, merged with the writes this session made after them: the newest of each load. */
    assigns: StoredAssigns
    /** Stored writes read from the tab. */
    read: StoredAssigns
    /** Writes this session made, not yet read back. */
    notes: StoredAssigns
    /** Reads the tab's stored writes again; a read for a chat no longer open is dropped. */
    load: (tabId: string | null) => Promise<void>
    /** Records a write the session just made on a load (a null target: taken off its truck). Ignored for a chat not open. */
    note: (tabId: string, loadId: string, target: AssignTarget | null) => void
}

const NONE: StoredAssigns = {}
let reads = 0

/** The open chat's stored assigns (the Agent page's assign and unassign writes). A failed read keeps what is shown. */
export const useStoredAssignStore = create<StoredAssignStore>((set, get) => ({
    tabId: null,
    assigns: NONE,
    read: NONE,
    notes: NONE,
    load: async (tabId) => {
        const mine = ++reads
        if (tabId !== get().tabId) set({ tabId, assigns: NONE, read: NONE, notes: NONE })
        if (!tabId) return
        try {
            const rows: GeneralChatMessage[] = await fetchGeneralWriteResults(tabId)
            if (mine !== reads) return
            const read = storedAssignsOf(rows)
            set((s) => ({ read, assigns: newestAssigns(read, s.notes) }))
        } catch {
            // the rows already shown stay
        }
    },
    note: (tabId, loadId, target) => {
        if (tabId !== get().tabId) return
        set((s) => {
            const notes = { ...s.notes, [loadId]: { at: Date.now(), target } }
            return { notes, assigns: newestAssigns(s.read, notes) }
        })
    },
}))

/** Keeps the open chat's stored assigns in step with it: read again whenever its stored messages change. */
export const useThreadStoredAssigns = (tabId: string | null, messages: GeneralChatMessage[]): StoredAssigns => {
    const load = useStoredAssignStore((s) => s.load)
    const assigns = useStoredAssignStore((s) => (s.tabId === tabId ? s.assigns : NONE))
    const storedKey = messages
        .filter((m) => !m.id.startsWith('local-'))
        .map((m) => m.id)
        .join(',')
    useEffect(() => {
        void load(tabId)
    }, [load, tabId, storedKey])
    return assigns
}
